"use client";

import { useRef, useState, FormEvent } from "react";
import Link from "next/link";
import { useShell } from "../../components/page-shell";
import { ValidationErrorTooltip } from "../../components/validation-error-tooltip";
import { Input } from "@carpybara/ui/shadcn/input";
import { Button } from "@carpybara/ui/shadcn/button";
import { Mail, ArrowLeft } from "lucide-react";
import { Spinner } from "@carpybara/ui";
import {
  AuthCard,
  AuthCardHeader,
  AuthInputField,
} from "@/components/auth/auth-form-primitives";

export default function ForgotPasswordPage() {
  const { t } = useShell();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailValidationMessage, setEmailValidationMessage] = useState<
    string | null
  >(null);
  const emailInputRef = useRef<HTMLInputElement | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const emailInput = emailInputRef.current;
    if (emailInput && !emailInput.validity.valid) {
      setError(null);
      setEmailValidationMessage(emailInput.validationMessage);
      return;
    }
    setEmailValidationMessage(null);
    setError(null);
    setBusy(true);
    try {
      const res = await fetch("/api/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        setError(data.error ?? t("auth.errors.generic"));
        setBusy(false);
        return;
      }
      setSent(true);
    } catch {
      setError(t("auth.errors.generic"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-md mx-auto py-12 px-4">
      <AuthCard>
        <AuthCardHeader
          title={t("auth.forgot.heading")}
          description={
            sent ? t("auth.forgot.sentSubheading") : t("auth.forgot.subheading")
          }
        />

        {sent ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-(--border) bg-(--bg-card) p-4 text-sm text-(--text-primary)">
              {t("auth.forgot.sentBody")}
            </div>
            <Link
              href="/login"
              className="flex items-center justify-center gap-1.5 text-sm text-(--accent) font-semibold hover:underline"
            >
              <span className="button-icon-motion-back inline-flex shrink-0">
                <ArrowLeft className="w-4 h-4" />
              </span>
              {t("auth.forgot.backToLogin")}
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3" noValidate>
            <div className="relative">
              <AuthInputField icon={<Mail className="h-4 w-4" />}>
                <Input
                  ref={emailInputRef}
                  type="email"
                  required
                  placeholder={t("auth.fields.emailPlaceholder")}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailValidationMessage(null);
                  }}
                  onInput={() => setEmailValidationMessage(null)}
                  autoComplete="email"
                  aria-invalid={emailValidationMessage ? true : undefined}
                  className={
                    emailValidationMessage
                      ? "border-red-500"
                      : undefined
                  }
                />
              </AuthInputField>
              <ValidationErrorTooltip message={emailValidationMessage} />
            </div>

            {error && (
              <p className="text-sm text-red-400" role="alert">
                {error}
              </p>
            )}

            <Button type="submit" className="w-full" disabled={busy}>
              {busy ? <Spinner size="sm" /> : t("auth.forgot.sendResetLink")}
            </Button>

            <p className="text-xs text-center text-(--text-muted)">
              <Link
                href="/login"
                className="inline-flex items-center gap-1 text-(--accent) font-semibold hover:underline"
              >
                <span className="button-icon-motion-back inline-flex shrink-0">
                  <ArrowLeft className="w-3 h-3" />
                </span>
                {t("auth.forgot.backToLogin")}
              </Link>
            </p>
          </form>
        )}
      </AuthCard>
    </div>
  );
}
